import { useEffect, useState } from "react";
import { ClipboardCheck, AlertTriangle, CheckCircle, ArrowLeft, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...init,
  });
  if (res.status === 204) return {} as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data?.message || "Request failed"), data);
  return data as T;
}

interface QueueRow {
  id: string;
  teamMemberId: string;
  teamMemberName: string;
  periodStart: string;
  periodEnd: string;
  deadlineDate?: string;
}

interface Queue {
  awaitingReview: QueueRow[];
  notYetResponded: QueueRow[];
}

interface DayEntry {
  workDate: string;
  clockInAt: string | null;
  clockOutAt: string | null;
  breakMinutes: number;
  hours: number | null;
}

interface Adjustment {
  id: string;
  workDate: string;
  field: string;
  requestedValue: string;
  reason: string;
  teamLeadDecision: "approved" | "edited" | "denied" | null;
  teamLeadOverrideValue: string | null;
  teamLeadNote: string | null;
  pccConfirmationStatus: "pending" | "confirmed" | "appealed" | null;
}

interface PeriodDetail {
  period: { id: string; periodStart: string; periodEnd: string; status: string };
  live: { days: DayEntry[]; totalHours: number };
  adjustments: Adjustment[];
}

const FIELD_LABELS: Record<string, string> = {
  clock_in: "Clock In", clock_out: "Clock Out", break_start: "Break Start", break_end: "Break End",
};

function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function DecisionPill({ adj }: { adj: Adjustment }) {
  if (!adj.teamLeadDecision) return <span className="text-xs text-gray-400">Awaiting decision</span>;
  const map: Record<string, { label: string; cls: string }> = {
    approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
    denied: { label: "Denied", cls: "bg-red-100 text-red-700" },
    edited: {
      label: adj.pccConfirmationStatus === "confirmed" ? "Edited — PCC confirmed"
        : adj.pccConfirmationStatus === "appealed" ? "Edited — PCC appealed"
        : "Edited — awaiting PCC",
      cls: adj.pccConfirmationStatus === "confirmed" ? "bg-emerald-100 text-emerald-700"
        : adj.pccConfirmationStatus === "appealed" ? "bg-red-100 text-red-700"
        : "bg-blue-100 text-blue-700",
    },
  };
  const { label, cls } = map[adj.teamLeadDecision];
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>;
}

function AdjustmentCard({ adj, periodId, onDecided }: { adj: Adjustment; periodId: string; onDecided: () => void }) {
  const [mode, setMode] = useState<"view" | "edit" | "deny">("view");
  const [overrideValue, setOverrideValue] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const decide = async (decision: "approve" | "edit" | "deny") => {
    setSubmitting(true);
    try {
      await apiRequest(`/api/v1/timesheet-review/team-lead/${periodId}/adjustments/${adj.id}/decision`, {
        method: "POST",
        body: JSON.stringify({
          decision,
          ...(decision === "edit" ? { overrideValue: new Date(overrideValue).toISOString(), note } : {}),
          ...(decision === "deny" ? { note } : {}),
        }),
      });
      toast.success("Decision saved");
      setMode("view");
      onDecided();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save decision");
    } finally {
      setSubmitting(false);
    }
  };

  const locked = !!adj.teamLeadDecision;

  return (
    <div className="border border-gray-100 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800">{formatDate(adj.workDate)} — {FIELD_LABELS[adj.field] ?? adj.field}</p>
          <p className="text-xs text-gray-400">Requested {formatTime(adj.requestedValue)} · "{adj.reason}"</p>
        </div>
        <DecisionPill adj={adj} />
      </div>

      {!locked && mode === "view" && (
        <div className="flex gap-2 pt-1">
          <button onClick={() => decide("approve")} disabled={submitting} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">Approve</button>
          <button onClick={() => setMode("edit")} disabled={submitting} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-500 text-blue-600 hover:bg-blue-50">Edit</button>
          <button onClick={() => setMode("deny")} disabled={submitting} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-400 text-red-600 hover:bg-red-50">Deny</button>
        </div>
      )}

      {!locked && mode === "edit" && (
        <div className="space-y-2 pt-1">
          <input type="datetime-local" value={overrideValue} onChange={(e) => setOverrideValue(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full" />
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note explaining the change (required)" rows={2}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full" />
          <div className="flex gap-2">
            <button onClick={() => decide("edit")} disabled={submitting || !overrideValue || !note.trim()} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">Save Edit</button>
            <button onClick={() => setMode("view")} className="px-3 py-1.5 text-xs text-gray-500">Cancel</button>
          </div>
        </div>
      )}

      {!locked && mode === "deny" && (
        <div className="space-y-2 pt-1">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for denial (required)" rows={2}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full" />
          <div className="flex gap-2">
            <button onClick={() => decide("deny")} disabled={submitting || !note.trim()} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">Confirm Denial</button>
            <button onClick={() => setMode("view")} className="px-3 py-1.5 text-xs text-gray-500">Cancel</button>
          </div>
        </div>
      )}

      {locked && adj.teamLeadDecision === "edited" && (
        <p className="text-xs text-gray-500">Your correction: <span className="font-medium text-blue-700">{formatTime(adj.teamLeadOverrideValue)}</span> — "{adj.teamLeadNote}"</p>
      )}
      {locked && adj.teamLeadDecision === "denied" && adj.teamLeadNote && (
        <p className="text-xs text-gray-500">Reason: "{adj.teamLeadNote}"</p>
      )}
    </div>
  );
}

export function TimesheetReviewTeamLead() {
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState<Queue>({ awaitingReview: [], notYetResponded: [] });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PeriodDetail | null>(null);
  const [finalizing, setFinalizing] = useState(false);

  const loadQueue = () => {
    setLoading(true);
    apiRequest<{ data: Queue }>("/api/v1/timesheet-review/team-lead/queue")
      .then((res) => setQueue(res.data))
      .catch(() => toast.error("Failed to load queue"))
      .finally(() => setLoading(false));
  };

  useEffect(loadQueue, []);

  const openPeriod = (id: string) => {
    setSelectedId(id);
    apiRequest<{ data: PeriodDetail }>(`/api/v1/timesheet-review/team-lead/${id}`)
      .then((res) => setDetail(res.data))
      .catch(() => toast.error("Failed to load period"));
  };

  const refreshDetail = () => {
    if (selectedId) openPeriod(selectedId);
  };

  const finalize = async () => {
    if (!selectedId) return;
    setFinalizing(true);
    try {
      const res = await apiRequest<{ data: { status: string; anomalyFlags: string[] } }>(
        `/api/v1/timesheet-review/team-lead/${selectedId}/finalize`, { method: "POST" }
      );
      if (res.data.status === "team_lead_approved") {
        toast.success("Finalized — no further review needed");
      } else {
        toast.success(`Finalized — routed to OM (${res.data.anomalyFlags.join(", ")})`);
      }
      setSelectedId(null);
      setDetail(null);
      loadQueue();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to finalize");
    } finally {
      setFinalizing(false);
    }
  };

  const readyToFinalize = detail?.adjustments.every(
    (a) => a.teamLeadDecision && (a.teamLeadDecision !== "edited" || a.pccConfirmationStatus === "confirmed")
  ) ?? true;

  if (selectedId && detail) {
    return (
      <div className="space-y-5">
        <button onClick={() => { setSelectedId(null); setDetail(null); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-4 w-4" /> Back to queue
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden max-w-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">
              {formatDate(detail.period.periodStart)} – {formatDate(detail.period.periodEnd)}
            </h2>
          </div>
          <div className="divide-y divide-gray-50">
            {detail.live.days.map((d) => (
              <div key={d.workDate} className="px-4 py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">{formatDate(d.workDate)}</p>
                  <p className="text-xs text-gray-400">{formatTime(d.clockInAt)} – {formatTime(d.clockOutAt)}</p>
                </div>
                <span className="text-sm font-semibold text-gray-700">{d.hours != null ? `${d.hours.toFixed(2)}h` : "—"}</span>
              </div>
            ))}
          </div>
          <div className="px-4 py-2.5 bg-gray-50 flex items-center justify-between border-t border-gray-100">
            <span className="text-sm font-semibold text-gray-600">Total</span>
            <span className="text-sm font-bold text-gray-900">{detail.live.totalHours.toFixed(2)}h</span>
          </div>
        </div>

        {detail.adjustments.length > 0 && (
          <div className="max-w-xl space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">Requested corrections</h3>
            {detail.adjustments.map((adj) => (
              <AdjustmentCard key={adj.id} adj={adj} periodId={selectedId} onDecided={refreshDetail} />
            ))}
          </div>
        )}

        <div className="max-w-xl">
          <button
            onClick={finalize}
            disabled={!readyToFinalize || finalizing}
            className="w-full px-4 py-2.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
          >
            {finalizing ? "Finalizing…" : "Finalize"}
          </button>
          {!readyToFinalize && (
            <p className="text-xs text-gray-400 mt-1.5">Every correction needs a decision, and any edit needs the PCC's confirmation, before finalizing.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <ClipboardCheck className="h-6 w-6 text-blue-500" /> Timesheet Review — Team Lead
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">Review your team's timesheet responses and requested corrections.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-10 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden max-w-2xl">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">Awaiting your review ({queue.awaitingReview.length})</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                These team members have already confirmed or requested edits on their own timesheet — click one to make your decision.
              </p>
            </div>
            {queue.awaitingReview.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-400 text-center">Nothing awaiting your review right now.</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {queue.awaitingReview.map((row) => (
                  <button key={row.id} onClick={() => openPeriod(row.id)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 text-left">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{row.teamMemberName}</p>
                      <p className="text-xs text-gray-400">{formatDate(row.periodStart)} – {formatDate(row.periodEnd)}</p>
                    </div>
                    <CheckCircle className="h-4 w-4 text-gray-300" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {queue.notYetResponded.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-red-100 overflow-hidden max-w-2xl">
              <div className="px-4 py-3 border-b border-red-100 bg-red-50">
                <h2 className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" /> Hasn't confirmed their timesheet yet ({queue.notYetResponded.length})
                </h2>
                <p className="text-xs text-red-600 mt-0.5">
                  These team members haven't clicked "This Is Correct" or "I Need to Request Edits" on their own
                  timesheet review email yet, so there's nothing here for you to review until they do.
                </p>
              </div>
              <div className="divide-y divide-gray-50">
                {queue.notYetResponded.map((row) => (
                  <div key={row.id} className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-800">{row.teamMemberName}</p>
                    <p className="text-xs text-red-500">{formatDate(row.periodStart)} – {formatDate(row.periodEnd)} · was due {formatDate(row.deadlineDate!)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
